import { useState, useMemo } from 'react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from 'recharts'
import { usePortfolioAnalyticsBook } from '@/hooks/useDemoDataAccess'
import { CHART_TOOLTIP_PROPS, chartAxisProps, chartGridProps, chartSeriesColor } from '@/config/chartTheme'

const fmt = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(2)}B` : `$${v.toFixed(1)}M`
const pct = (v: number, d = 1) => `${(v * 100).toFixed(d)}%`

import type { Loan } from '@/types'

// Monte Carlo-style VaR simulation using parametric approach
function computeVaR(deals: Loan[], confidenceLevel: number, holdingPeriodDays: number) {
  const totalValue = deals.reduce((s, d) => s + (d.propertyValue ?? d.loanAmount / (d.ltv || 0.70)), 0)
  const totalDebt = deals.reduce((s, d) => s + d.loanAmount, 0)

  // Estimated portfolio volatility based on asset mix (annualized)
  const volByType: Record<string, number> = {
    Multifamily: 0.08,
    Office: 0.14,
    Industrial: 0.09,
    Retail: 0.12,
    Hospitality: 0.18,
    'Lab/R&D': 0.11,
    'Mixed-Use': 0.10,
    'Mixed Use': 0.10, // the client's spelling (Helios_Field Categories.xlsx); same weight
    Land: 0.22,
    'Land and Lots': 0.22,
    'Senior Living': 0.10,
    Construction: 0.20,
    'Self-Storage': 0.09,
    'Student Housing': 0.10,
  }

  // Weighted portfolio volatility
  let wtdVol = 0
  deals.forEach((d) => {
    const w = (d.propertyValue ?? d.loanAmount / (d.ltv || 0.70)) / totalValue
    wtdVol += w * (volByType[d.type] ?? 0.12)
  })

  // Diversification benefit (correlation < 1)
  const diversificationFactor = 0.72
  const portfolioVol = wtdVol * diversificationFactor

  // Z-score for confidence level
  const zScores: Record<number, number> = { 90: 1.282, 95: 1.645, 99: 2.326 }
  const z = zScores[confidenceLevel] ?? 1.645

  // Scale volatility to holding period
  const periodVol = portfolioVol * Math.sqrt(holdingPeriodDays / 365)

  // VaR calculations
  const varAbsolute = totalValue * z * periodVol
  const varPct = z * periodVol
  const expectedShortfall = totalValue * periodVol * (Math.exp(-z * z / 2) / (Math.sqrt(2 * Math.PI) * (1 - confidenceLevel / 100)))

  // Component VaR by asset type
  const typeMap = new Map<string, { value: number; vol: number }>()
  deals.forEach((d) => {
    const val = d.propertyValue ?? d.loanAmount / (d.ltv || 0.70)
    const existing = typeMap.get(d.type) ?? { value: 0, vol: 0 }
    typeMap.set(d.type, { value: existing.value + val, vol: volByType[d.type] ?? 0.12 })
  })
  const componentVar = [...typeMap.entries()].map(([type, data]) => ({
    type,
    value: data.value,
    vol: data.vol,
    var: data.value * z * data.vol * Math.sqrt(holdingPeriodDays / 365) * diversificationFactor,
    pctOfTotal: data.value / totalValue,
  })).sort((a, b) => b.var - a.var)

  // Loss distribution (simulated)
  const distribution = []
  for (let loss = 0; loss <= 30; loss += 1) {
    const prob = Math.exp(-(loss / 100) * (loss / 100) / (2 * periodVol * periodVol)) / (periodVol * Math.sqrt(2 * Math.PI))
    distribution.push({ loss: `-${loss}%`, probability: Math.round(prob * 10000) / 100 })
  }

  // Stress scenarios
  const stressScenarios = [
    { name: 'Mild Recession', capExpansion: 50, noiDecline: 5, probability: 25 },
    { name: 'Moderate Downturn', capExpansion: 100, noiDecline: 10, probability: 10 },
    { name: 'Severe Recession', capExpansion: 200, noiDecline: 20, probability: 3 },
    { name: 'GFC-Level Crisis', capExpansion: 350, noiDecline: 30, probability: 1 },
  ].map((s) => {
    let stressedValue = 0
    deals.forEach((d) => {
      const origVal = d.propertyValue ?? d.loanAmount / (d.ltv || 0.70)
      const noi = d.cashFlows.prospectiveNOI
      const origCap = noi / origVal
      const stressedNoi = noi * (1 - s.noiDecline / 100)
      const stressedCap = origCap + s.capExpansion / 10000
      stressedValue += stressedNoi / stressedCap
    })
    return {
      ...s,
      portfolioLoss: totalValue - stressedValue,
      portfolioLossPct: (totalValue - stressedValue) / totalValue,
      equityLoss: Math.max(0, totalDebt - stressedValue),
      resultingLtv: totalDebt / stressedValue,
    }
  })

  return {
    totalValue,
    totalDebt,
    portfolioVol,
    periodVol,
    varAbsolute,
    varPct,
    expectedShortfall,
    componentVar,
    distribution,
    stressScenarios,
  }
}

export default function ValueAtRiskModel() {
  const portfolioDeals = usePortfolioAnalyticsBook().complete
  const [confidence, setConfidence] = useState(95)
  const [holdingPeriod, setHoldingPeriod] = useState(90)

  const data = useMemo(
    () => computeVaR(portfolioDeals, confidence, holdingPeriod),
    // The live book arrives and refreshes after mount; VaR must follow it.
    [portfolioDeals, confidence, holdingPeriod],
  )

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="panel">
        <div className="panel-body flex items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-stone-400">Confidence Level:</span>
            <div className="flex gap-1">
              {[90, 95, 99].map((cl) => (
                <button
                  key={cl}
                  onClick={() => setConfidence(cl)}
                  className={`rounded-md px-3 py-1 text-[11px] font-medium transition-all ${
                    confidence === cl
                      ? 'bg-amber-400/15 text-amber-200 border border-amber-400/25'
                      : 'text-stone-500 hover:text-stone-300 border border-transparent'
                  }`}
                >
                  {cl}%
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-stone-400">Holding Period:</span>
            <div className="flex gap-1">
              {[30, 90, 180, 365].map((hp) => (
                <button
                  key={hp}
                  onClick={() => setHoldingPeriod(hp)}
                  className={`rounded-md px-3 py-1 text-[11px] font-medium transition-all ${
                    holdingPeriod === hp
                      ? 'bg-amber-400/15 text-amber-200 border border-amber-400/25'
                      : 'text-stone-500 hover:text-stone-300 border border-transparent'
                  }`}
                >
                  {hp}d
                </button>
              ))}
            </div>
          </div>
          <span className="ml-auto text-[10px] text-stone-500">
            Suggested model inputs: volatility by asset class and a 0.72 diversification factor are
            assumptions, not observed market data.
          </span>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-5 gap-3">
        <div className="metric-cell">
          <div className="metric-cell-label">Portfolio Value</div>
          <div className="metric-cell-value">{fmt(data.totalValue)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">VaR ({confidence}%, {holdingPeriod}d)</div>
          <div className="metric-cell-value text-red-400">{fmt(data.varAbsolute)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">VaR %</div>
          <div className="metric-cell-value text-red-400">{pct(data.varPct)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Expected Shortfall (CVaR)</div>
          <div className="metric-cell-value text-red-400">{fmt(data.expectedShortfall)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Portfolio Volatility (Ann.)</div>
          <div className="metric-cell-value">{pct(data.portfolioVol)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Component VaR by Asset Type */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">Component VaR by Asset Type</span>
          </div>
          <div className="panel-body">
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.componentVar} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                  <CartesianGrid {...chartGridProps()} />
                  <XAxis dataKey="type" {...chartAxisProps(9)} />
                  <YAxis {...chartAxisProps(9)} tickFormatter={(v: number) => `$${v.toFixed(0)}M`} />
                  <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v: number) => [fmt(v), 'VaR']} />
                  <Bar dataKey="var" radius={[4, 4, 0, 0]}>
                    {data.componentVar.map((_, i) => (
                      <Cell key={i} fill={chartSeriesColor(i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Stress-Loss Scenarios */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">Stress-Loss Scenarios</span>
          </div>
          <div className="panel-body">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th className="text-right">Cap +</th>
                  <th className="text-right">NOI -</th>
                  <th className="text-right">Loss</th>
                  <th className="text-right">Loss %</th>
                  <th className="text-right">LTV</th>
                  <th className="text-right">Prob.</th>
                </tr>
              </thead>
              <tbody>
                {data.stressScenarios.map((s) => (
                  <tr key={s.name}>
                    <td className="text-stone-200 font-medium">{s.name}</td>
                    <td className="text-right font-mono text-stone-300">+{s.capExpansion}bp</td>
                    <td className="text-right font-mono text-stone-300">-{s.noiDecline}%</td>
                    <td className="text-right font-mono text-red-400">{fmt(s.portfolioLoss)}</td>
                    <td className={`text-right font-mono font-bold ${s.portfolioLossPct > 0.30 ? 'text-red-400' : s.portfolioLossPct > 0.15 ? 'text-amber-400' : 'text-stone-300'}`}>
                      -{pct(s.portfolioLossPct)}
                    </td>
                    <td className={`text-right font-mono ${s.resultingLtv > 0.90 ? 'text-red-400 font-bold' : s.resultingLtv > 0.75 ? 'text-amber-400' : 'text-stone-300'}`}>
                      {pct(s.resultingLtv)}
                    </td>
                    <td className="text-right font-mono text-stone-500">{s.probability}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Loss Distribution */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Loss Distribution (Parametric)</span>
          <span className="text-[10px] text-stone-600 font-mono">VaR at {confidence}% = {pct(data.varPct)}</span>
        </div>
        <div className="panel-body">
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.distribution} margin={{ left: 8, right: 16, top: 8, bottom: 4 }}>
                <CartesianGrid {...chartGridProps()} />
                <XAxis dataKey="loss" {...chartAxisProps(9)} />
                <YAxis {...chartAxisProps(9)} />
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v: number) => [`${v.toFixed(1)}%`, 'Probability Density']} />
                <Line type="monotone" dataKey="probability" stroke={chartSeriesColor(0)} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}